import { Alert, Button, Flex, HStack, SkeletonCircle, SkeletonText, Stack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '@/components/panel';
import AddPaymentMethodModal from '@/components/payment-methods/AddPaymentMethodModal';
import { SaveDefaultCheckbox } from '@/components/payment-methods/SaveDefaultCheckbox';
import { SelectedPaymentMethodDisplay } from '@/components/payment-methods/SelectedPaymentMethodDisplay';
import { useBrandPaymentMethodsQuery } from '@/hooks/queries/use-brand-payment-methods-query';
import { usePayoutCheckoutStore } from '@/stores/payout-checkout-store';
import { SchedulePayoutSection } from './SchedulePayoutSection';
import { SchedulePayoutSectionSkeleton } from './SchedulePayoutSectionSkeleton';

const PaymentMethodPanel = () => {
  const { t } = useTranslation();
  const [isAddPaymentMethodModalOpen, setIsAddPaymentMethodModalOpen] = useState(false);

  const { selectedPaymentMethodId, setSelectedPaymentMethodId, saveAsDefault, setSaveAsDefault } =
    usePayoutCheckoutStore();

  // Fetch payment methods using React Query
  const {
    data: paymentMethodsData,
    isLoading: isLoadingPaymentMethods,
    error: paymentMethodsError,
  } = useBrandPaymentMethodsQuery();

  const paymentMethods = paymentMethodsData?.paymentMethods || [];

  // Effect to set default payment method
  useEffect(() => {
    // Only run if data is loaded, we have methods, and nothing is selected yet
    if (paymentMethodsData && paymentMethods.length > 0 && !selectedPaymentMethodId) {
      const defaultMethod = paymentMethods.find((pm) => pm.isDefault);
      if (defaultMethod) {
        setSelectedPaymentMethodId(defaultMethod.id);
      }
    }
  }, [paymentMethodsData, paymentMethods, selectedPaymentMethodId, setSelectedPaymentMethodId]);

  const selectedPaymentMethod = paymentMethods.find((pm) => pm.id === selectedPaymentMethodId);

  const handleConfirmPaymentMethod = (id: string) => {
    setSelectedPaymentMethodId(id);
    setIsAddPaymentMethodModalOpen(false);
    setSaveAsDefault(false);
  };

  const handleCheckboxChange = (details: { checked: boolean | 'indeterminate' }) => {
    setSaveAsDefault(!!details.checked);
  };

  const showSaveDefaultCheckbox =
    !isLoadingPaymentMethods &&
    !paymentMethodsError &&
    selectedPaymentMethod &&
    (!selectedPaymentMethod.isDefault || saveAsDefault);

  return (
    <Panel title={t('payouts.checkout.paymentMethod.title')}>
      <Stack gap={4} align="stretch" direction="column">
        {/* Error Alert */}
        {paymentMethodsError && (
          <Alert.Root status="error" rounded="lg">
            <Alert.Indicator />
            <Alert.Description>
              {t('payouts.checkout.paymentMethod.errorFetching', {
                message: paymentMethodsError.message,
              })}
            </Alert.Description>
          </Alert.Root>
        )}

        {/* Payment Method Display or Skeleton */}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          border="1px solid"
          borderColor="gray.200"
          rounded="xl"
          p={3}
          minH="60px"
        >
          {isLoadingPaymentMethods ? (
            // Skeleton Layout
            <Flex justify="space-between" w="full" align="center">
              <HStack gap={2} flex={1}>
                <SkeletonCircle size={8} />
                <SkeletonText noOfLines={1} h="20px" width="180px" />
              </HStack>
              <SkeletonText rootProps={{ w: '100px' }} noOfLines={1} h="30px" />
            </Flex>
          ) : !paymentMethodsError ? (
            // Actual Content
            <>
              <SelectedPaymentMethodDisplay paymentMethod={selectedPaymentMethod} />
              <Button
                size="sm"
                variant="subtle"
                colorPalette="gray"
                onClick={() => setIsAddPaymentMethodModalOpen(true)}
              >
                {selectedPaymentMethodId
                  ? t('payouts.checkout.paymentMethod.change')
                  : t('payouts.checkout.paymentMethod.add')}
              </Button>
            </>
          ) : null}
        </Flex>

        {/* Conditionally render SaveDefaultCheckbox */}
        {showSaveDefaultCheckbox && (
          <SaveDefaultCheckbox isChecked={saveAsDefault} onChange={handleCheckboxChange} />
        )}

        {/* Render the Schedule Payout Section or its Skeleton */}
        {isLoadingPaymentMethods ? <SchedulePayoutSectionSkeleton /> : <SchedulePayoutSection />}
      </Stack>

      <AddPaymentMethodModal
        isOpen={isAddPaymentMethodModalOpen}
        onClose={() => setIsAddPaymentMethodModalOpen(false)}
        onConfirm={handleConfirmPaymentMethod}
        currentPaymentMethodId={selectedPaymentMethodId}
      />
    </Panel>
  );
};
export default PaymentMethodPanel;
